import { useEffect, useState } from "react";
import { General } from "../util/General";
import useValue from "./useValue";
import * as mobileUserManagementService from "../services/mobileUserManagementService";
import { CustomNotification } from "../util/CustomNotification";
import type { GetAllMobileUserResponse } from "../shared/interface/UserManagement/mobileUser.interface";
import useTablePagination from "./useTablePagination";
import { TableHelper } from "../util/TableHelper";
import { useSelector } from "react-redux";
import { RootState } from "../appRedux/store";
import { UserTypeConstants } from "../constants/DatabaseConstants/MasterConstant";

const useGetMobileUserList = (userType?: UserTypeConstants) => {
  const [userList, setUserList] = useState<GetAllMobileUserResponse[]>([]);
  const [reFetchOrderList, setRefetchOrderList] = useState(General.getGUID());
  const [filteredTableData, setFilteredTableData] = useState<GetAllMobileUserResponse[]>([]);

  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(true);

  /* CUSTOM HOOKS */
  const { pagination, handleChangePagination, handleChangeTotalSize } = useTablePagination(0);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HANDLERS */
  const handleRefetchUserList = () => setRefetchOrderList(General.getGUID());

  const handleFilterTableData = (searchedText: string | undefined) => {
    const keys = ["full_name", "email_address", "mobile_number"];
    const result = TableHelper.getFilterDataSource(userList, keys, searchedText) as GetAllMobileUserResponse[];

    /* update filter state */
    setFilteredTableData(result);

    /* update pagination */
    handleChangeTotalSize(result?.length);
  };

  const checkIsVisitorValues = (allocation: { is_visitor: boolean }[], isVisitor: boolean) => {
    return allocation?.some((item) => item?.is_visitor === isVisitor) || false;
  };

  /* EFFECTS */
  useEffect(() => {
    const fetchUserList = async () => {
      handleEnableLoading();

      const showDeleted = true;
      const data = await mobileUserManagementService.getAllUserList(showDeleted, masterFilters?.customerId, masterFilters?.locationId, userType);

      /* add key to each item in array */
      const formattedData = data?.["users"]?.map((item, index) => {
        return {
          ...item,
          sr_no: index + 1,
          client_name: item?.customerClient?.client_name,
          full_name: `${item?.first_name ?? ""} ${item?.last_name ?? ""}`,
          isVisitorUser: checkIsVisitorValues(item?.keyFobAllocation, true),
          isRegularUser: checkIsVisitorValues(item?.keyFobAllocation, false)
        };
      });

      setUserList(formattedData);
      setFilteredTableData(formattedData);
      handleChangeTotalSize(formattedData?.length);
    };

    fetchUserList()
      .catch((error) => {
        CustomNotification.showErrorMessage(error);
      })
      .finally(() => {
        handleDisableLoading();
      });

    // eslint-disable-next-line
  }, [reFetchOrderList, masterFilters?.customerId, masterFilters?.locationId, userType]);

  return { loading, userList, filteredTableData, handleRefetchUserList, pagination, handleChangePagination, handleFilterTableData };
};

export default useGetMobileUserList;
